const { useEffect, useState } = React;
import { dbSetSponsors, dbSubscribeSponsors } from "./data.jsx";
import { useT } from "./i18n.jsx";
import { uid } from "./model.jsx";
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus, Save, StarIcon, T, Trash2 } from "./theme.jsx";

// ========================================================= SPONSOR ACTIVE =====
// The sponsors that must run on the LED RING & LED T, one list per show
// category (the weekday's `category`), split into during-the-fight and
// between-rounds. Everyone signed in reads it; producers edit it.

const SPONSOR_CATS = [
  { id: "promoter", category: "Promoter", label: "spCatPromoter" },
  { id: "rko", category: "RKO", label: "spCatRko" },
  { id: "rws", category: "RWS", label: "spCatRws" },
];
const sponsorCatOf = (wd) => (SPONSOR_CATS.find((c) => c.category === (wd && wd.category)) || SPONSOR_CATS[0]).id;
const LISTS = [{ key: "fight", label: "spFight", sub: "LED RING & LED T" }, { key: "break", label: "spBreak", sub: "LED RING & LED T" }];

const newRow = () => ({ id: uid(), brand: "", type: "", rounds: "1/1", secs: 5, note: "", hl: false });
const sumSecs = (rows) => (rows || []).reduce((n, r) => n + (Number(r.secs) || 0), 0);
function fmtDuration(secs, t) {
  const m = Math.floor(secs / 60), s = secs % 60;
  if (!m) return `${s} ${t("spSec")}`;
  return s ? `${m} ${t("spMin")} ${String(s).padStart(2, "0")} ${t("spSec")}` : `${m} ${t("spMin")}`;
}
const fmtDate = (ms) => { const d = new Date(ms); return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`; };

const HL_BG = "#FFD9B8";
const cell = { padding: "7px 8px", borderBottom: `1px solid ${T.border}`, fontSize: 13, color: T.text, verticalAlign: "middle" };
const head = { ...cell, fontSize: 11, fontWeight: 800, color: T.subtext, letterSpacing: 0.3, textAlign: "left", background: T.panel2 };
const inputStyle = { fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 7px", background: T.panel, color: T.text, minWidth: 0, width: "100%", boxSizing: "border-box" };
const iconBtn = { background: "none", border: "none", cursor: "pointer", padding: 3, color: T.subtext, display: "flex" };

function SponsorTable({ rows }) {
  const { t } = useT();
  if (!rows.length) return <div style={{ padding: 14, fontSize: 12.5, color: T.subtext }}>{t("spEmpty")}</div>;
  return (
    <div className="rb-scroll" style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", minWidth: 440, borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={{ ...head, width: 26, textAlign: "center" }}>#</th>
          <th style={head}>{t("spBrand")}</th>
          <th style={head}>{t("spType")}</th>
          <th style={{ ...head, textAlign: "center" }}>{t("spRounds")}</th>
          <th style={{ ...head, textAlign: "center" }}>{t("spSecs")}</th>
          <th style={head}>{t("spNote")}</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} data-sponsor-row={r.brand} style={{ background: r.hl ? HL_BG : "transparent" }}>
              <td style={{ ...cell, textAlign: "center", color: T.subtext, fontWeight: 700 }}>{i + 1}</td>
              <td style={{ ...cell, fontWeight: 800 }}>{r.brand}</td>
              <td style={{ ...cell, fontSize: 12 }}>{r.type}</td>
              <td style={{ ...cell, textAlign: "center", fontWeight: 700 }}>{r.rounds}</td>
              <td style={{ ...cell, textAlign: "center", fontWeight: 700, whiteSpace: "nowrap" }}>{Number(r.secs) || 0} {t("spSecUnit")}</td>
              <td style={{ ...cell, fontSize: 12, fontWeight: r.hl ? 700 : 400 }}>{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SponsorEditor({ rows, onChange }) {
  const { t } = useT();
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i, d) => {
    const next = [...rows];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((r, i) => (
        <div key={r.id} data-sponsor-edit={i} style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", padding: 6, borderRadius: 8, background: r.hl ? HL_BG : T.panel2 }}>
          <span style={{ width: 18, fontSize: 12, fontWeight: 800, color: T.subtext, textAlign: "center" }}>{i + 1}</span>
          <input aria-label={t("spBrand")} placeholder={t("spBrand")} className="rb-input" value={r.brand} onChange={(e) => set(i, { brand: e.target.value })} style={{ ...inputStyle, flex: "2 1 130px", width: "auto", fontWeight: 700 }} />
          <input aria-label={t("spType")} placeholder={t("spType")} className="rb-input" value={r.type} onChange={(e) => set(i, { type: e.target.value })} style={{ ...inputStyle, flex: "1.5 1 110px", width: "auto" }} />
          <input aria-label={t("spRounds")} placeholder={t("spRounds")} className="rb-input" value={r.rounds} onChange={(e) => set(i, { rounds: e.target.value })} style={{ ...inputStyle, flex: "0 0 52px", textAlign: "center" }} />
          <span style={{ display: "flex", alignItems: "center", gap: 3, flex: "0 0 76px" }}>
            <input aria-label={t("spSecs")} type="number" min="0" className="rb-input" value={r.secs} onChange={(e) => set(i, { secs: Math.max(0, parseInt(e.target.value, 10) || 0) })} style={{ ...inputStyle, textAlign: "center" }} />
            <span style={{ fontSize: 11, color: T.subtext }}>{t("spSecUnit")}</span>
          </span>
          <input aria-label={t("spNote")} placeholder={t("spNote")} className="rb-input" value={r.note} onChange={(e) => set(i, { note: e.target.value })} style={{ ...inputStyle, flex: "2 1 120px", width: "auto" }} />
          <span style={{ display: "flex", gap: 1 }}>
            <button className="rb-btn" title={t("spHl")} aria-label={`${t("spHl")}: ${r.brand}`} aria-pressed={!!r.hl} onClick={() => set(i, { hl: !r.hl })} style={{ ...iconBtn, color: r.hl ? T.accentInk : T.subtext }}><StarIcon size={14} /></button>
            <button className="rb-btn" title={t("spMoveUp")} aria-label={t("spMoveUp")} disabled={i === 0} onClick={() => move(i, -1)} style={{ ...iconBtn, opacity: i === 0 ? 0.3 : 1 }}><ChevronUp size={14} /></button>
            <button className="rb-btn" title={t("spMoveDown")} aria-label={t("spMoveDown")} disabled={i === rows.length - 1} onClick={() => move(i, 1)} style={{ ...iconBtn, opacity: i === rows.length - 1 ? 0.3 : 1 }}><ChevronDown size={14} /></button>
            <button className="rb-btn" title={t("deleteBtn")} aria-label={`${t("deleteBtn")}: ${r.brand}`} onClick={() => onChange(rows.filter((_, j) => j !== i))} style={{ ...iconBtn, color: T.danger }}><Trash2 size={14} /></button>
          </span>
        </div>
      ))}
      <button className="rb-btn" onClick={() => onChange([...rows, newRow()])}
        style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 4, background: T.panel2, color: T.text, border: `1px dashed ${T.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
        <Plus size={12} /> {t("spAddRow")}
      </button>
    </div>
  );
}

function SponsorPanel({ canEdit, todayWd }) {
  const { t } = useT();
  const todayCat = sponsorCatOf(todayWd);
  const [cat, setCat] = useState(todayCat);
  const [doc, setDoc] = useState(undefined); // undefined = loading, null = none yet
  const [draft, setDraft] = useState(null);  // non-null while editing
  const [saveState, setSaveState] = useState("idle");

  useEffect(() => {
    setDoc(undefined); setDraft(null); setSaveState("idle");
    return dbSubscribeSponsors(cat, (d) => setDoc(d), () => setDoc((v) => (v === undefined ? null : v)));
  }, [cat]);

  const data = draft || doc || {};
  const startEdit = () => setDraft({
    fight: (doc && doc.fight) || [], break: (doc && doc.break) || [],
    fightTotal: (doc && doc.fightTotal) || "", breakTotal: (doc && doc.breakTotal) || "",
  });
  const save = async () => {
    setSaveState("saving");
    const clean = (rows) => rows.filter((r) => r.brand.trim()).map((r) => ({ ...r, brand: r.brand.trim() }));
    const next = { ...draft, fight: clean(draft.fight), break: clean(draft.break) };
    const ok = await dbSetSponsors(cat, next);
    // show what was saved right away; the live feed catches up a moment later
    if (ok) { setDoc({ ...next, updatedAt: Date.now() }); setDraft(null); setSaveState("idle"); } else setSaveState("error");
  };

  return (
    <section data-sponsor-panel style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, marginBottom: 22, overflow: "hidden" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "14px 16px", background: T.dark, color: T.darkText }}>
        <div style={{ flex: "1 1 200px", minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 900, letterSpacing: 0.2 }}>{t("spTitle")}</div>
          <div style={{ fontSize: 11.5, color: T.darkSubtext, marginTop: 2 }}>
            {t("spIntro")}{doc && doc.updatedAt ? ` · ${t("spUpdated")} ${fmtDate(doc.updatedAt)}` : ""}
          </div>
        </div>
        <div role="tablist" style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {SPONSOR_CATS.map((c) => (
            <button key={c.id} role="tab" aria-selected={cat === c.id} data-sponsor-cat={c.id} className="rb-btn" onClick={() => setCat(c.id)}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 11px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 800, background: cat === c.id ? T.accent : T.darkChip, color: cat === c.id ? T.accentText : T.darkText }}>
              {t(c.label)}
              {c.id === todayCat && <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 5px", borderRadius: 4, background: cat === c.id ? T.dark : T.accent, color: cat === c.id ? T.darkText : T.accentText }}>{t("today")}</span>}
            </button>
          ))}
        </div>
        {canEdit && doc !== undefined && (draft ? (
          <span style={{ display: "flex", gap: 6 }}>
            <button className="rb-btn" onClick={() => { setDraft(null); setSaveState("idle"); }} style={{ background: T.darkChip, color: T.darkText, border: "none", borderRadius: 8, padding: "6px 11px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{t("cancel")}</button>
            <button className="rb-btn" onClick={save} disabled={saveState === "saving"} style={{ display: "flex", alignItems: "center", gap: 5, background: T.accent, color: T.accentText, border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
              {saveState === "saving" ? <Loader2 size={13} style={{ animation: "rb-spin 1s linear infinite" }} /> : <Save size={13} />} {t("spSave")}
            </button>
          </span>
        ) : (
          <button className="rb-btn" onClick={startEdit} style={{ display: "flex", alignItems: "center", gap: 5, background: T.darkChip, color: T.darkText, border: "none", borderRadius: 8, padding: "6px 11px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            <Pencil size={13} /> {t("spEdit")}
          </button>
        ))}
      </div>
      {saveState === "error" && <div role="alert" style={{ padding: "8px 16px", background: T.dangerBg, color: T.danger, fontSize: 12.5, fontWeight: 700 }}>{t("saveFailed")}</div>}

      {doc === undefined ? (
        <div style={{ padding: 24, textAlign: "center", color: T.subtext }}><Loader2 size={18} style={{ animation: "rb-spin 1s linear infinite" }} /></div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", gap: 12, padding: 12 }}>
          {LISTS.map((l) => {
            const rows = data[l.key] || [];
            const auto = sumSecs(rows);
            const total = data[`${l.key}Total`] || fmtDuration(auto, t);
            return (
              <div key={l.key} data-sponsor-list={l.key} style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.panel, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "10px 12px", borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 16, fontWeight: 900, color: T.text }}>{t(l.label)}</span>
                  <span style={{ fontSize: 11.5, color: T.subtext, fontWeight: 600 }}>{l.sub}</span>
                  <span style={{ marginLeft: "auto", fontSize: 12, color: T.subtext, fontWeight: 700 }}>{rows.filter((r) => r.brand).length} {t("spBrands")}</span>
                </div>
                <div style={{ flex: 1 }}>
                  {draft ? <SponsorEditor rows={rows} onChange={(next) => setDraft({ ...draft, [l.key]: next })} /> : <SponsorTable rows={rows} />}
                </div>
                {draft ? (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: T.panel2, fontSize: 12, fontWeight: 700, color: T.text }}>
                    {t("spTotal")}
                    <input aria-label={`${t("spTotal")} ${t(l.label)}`} className="rb-input" value={draft[`${l.key}Total`] || ""} placeholder={`${t("spTotalPh")} (${fmtDuration(auto, t)})`}
                      onChange={(e) => setDraft({ ...draft, [`${l.key}Total`]: e.target.value })} style={{ ...inputStyle, flex: 1, width: "auto" }} />
                  </label>
                ) : (
                  <div data-sponsor-total={l.key} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 12px", background: T.dark, color: T.darkText }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: T.darkSubtext, letterSpacing: 0.3 }}>{t("spTotal").toUpperCase()}</span>
                    <span style={{ fontSize: 19, fontWeight: 900, color: "#FFD60A" }}>{total}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { SponsorPanel, SPONSOR_CATS, sponsorCatOf, fmtDuration };
